import { RefObject, useEffect, useLayoutEffect, useState } from 'react';

// Tracks a scroll container's horizontal offset, rAF-throttled so a fast scroll
// yields at most one state update per frame. Kept in its own hook so each
// windowed overlay re-renders on scroll without re-rendering the whole timeline.
// `syncTo` snaps the tracked value to a programmatic scroll target before paint
// (e.g. a timeframe switch), so the overlay re-windows to the new position in
// the same frame the container scrolls, instead of an rAF later.
export const useTimelineScrollLeft = (
  scrollRef: RefObject<HTMLDivElement>,
  syncTo?: number,
): number => {
  const [scrollLeft, setScrollLeft] = useState(0);

  useLayoutEffect(() => {
    if (syncTo !== undefined) {
      setScrollLeft(syncTo);
    }
  }, [syncTo]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    let frame = 0;
    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setScrollLeft(element.scrollLeft));
    };
    element.addEventListener('scroll', handleScroll, { passive: true });
    setScrollLeft(element.scrollLeft);
    return () => {
      cancelAnimationFrame(frame);
      element.removeEventListener('scroll', handleScroll);
    };
  }, [scrollRef]);

  return scrollLeft;
};
